import { CatalogStore } from './catalog/CatalogStore';
import { CatalogView } from './catalog/CatalogView';
import { CameraView } from './camera/CameraView';
import { PlayerView } from './player/PlayerView';
import type { ScoreItem } from './models/ScoreItem';
import { el } from './dom';

type Tab = 'library' | 'camera';

/**
 * Two tabs (Library and Camera) with the player taking the screen over when a
 * score is opened, the way the iOS `fullScreenCover` does.
 */
export class App {
  private readonly store: CatalogStore;
  private readonly root: HTMLElement;
  private readonly library: HTMLElement;
  private readonly camera: HTMLElement;
  private readonly player: PlayerView;
  private readonly tabButtons: Record<Tab, HTMLButtonElement>;

  constructor(host: HTMLElement, store: CatalogStore) {
    this.store = store;
    this.root = el('div', { class: 'app' });
    const shell = el('div', { class: 'app-shell' });
    const body = el('div', { class: 'app-body' });
    this.library = el('section', { class: 'tab-panel', id: 'tab-library', role: 'tabpanel' });
    this.camera = el('section', { class: 'tab-panel', id: 'tab-camera', role: 'tabpanel', hidden: 'hidden' });
    body.append(this.library, this.camera);

    const bar = el('nav', { class: 'tab-bar', role: 'tablist', 'aria-label': 'Main' });
    this.tabButtons = {
      library: tabButton('library', 'Library', '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M4 5h7v14H4zm9 0h7v14h-7z"/></svg>'),
      camera: tabButton('camera', 'Camera', '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M9 4h6l2 3h4v13H3V7h4z"/><circle cx="12" cy="13" r="3.5" fill="#fff"/></svg>'),
    };
    this.tabButtons.library.addEventListener('click', () => this.setTab('library'));
    this.tabButtons.camera.addEventListener('click', () => this.setTab('camera'));
    bar.append(this.tabButtons.library, this.tabButtons.camera);

    shell.append(body, bar);
    this.root.append(shell);
    host.append(this.root);

    this.player = new PlayerView(this.root);
    new CatalogView(this.library, store, (item) => void this.play(item));
    new CameraView(this.camera, store);
    this.setTab('library');
  }

  private setTab(tab: Tab): void {
    this.library.hidden = tab !== 'library';
    this.camera.hidden = tab !== 'camera';
    this.tabButtons.library.setAttribute('aria-selected', tab === 'library' ? 'true' : 'false');
    this.tabButtons.camera.setAttribute('aria-selected', tab === 'camera' ? 'true' : 'false');
  }

  private async play(item: ScoreItem): Promise<void> {
    this.root.classList.add('is-playing');
    const xml = await this.store.xmlText(item);
    await this.player.open(xml, () => {
      this.root.classList.remove('is-playing');
    });
  }
}

function tabButton(id: Tab, label: string, svg: string): HTMLButtonElement {
  const button = el('button', {
    type: 'button',
    role: 'tab',
    id: `tab-btn-${id}`,
    'aria-controls': `tab-${id}`,
    'aria-selected': 'false',
  });
  button.innerHTML = `${svg}<span>${label}</span>`;
  return button;
}
